import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Hook to get localized content from a database record
 * Falls back to English content if Arabic is not available
 */
export const useLocalizedContent = () => {
  const { isArabic } = useLanguage();

  /**
   * Get localized field value
   * @param {Object} record - Database record
   * @param {string} field - Field name (e.g., 'title', 'excerpt', 'content_html')
   * @returns {string} Localized value or English fallback
   */
  const getField = (record, field) => {
    if (!record) return '';
    
    if (isArabic) {
      const arField = `${field}_ar`;
      // Return Arabic if available, otherwise fall back to English
      return record[arField] || record[field] || '';
    }
    
    return record[field] || '';
  };

  /**
   * Get localized JSON content (for page_sections)
   * @param {Object} record - Database record with content/content_ar fields
   * @returns {Object} Localized content object
   */
  const getContent = (record) => {
    if (!record) return {};
    
    if (isArabic) {
      // Merge English content with Arabic overrides
      const enContent = record.content || {};
      const arContent = record.content_ar || {};
      
      // If Arabic content exists, use it; otherwise fall back to English
      if (Object.keys(arContent).length > 0) {
        return { ...enContent, ...arContent };
      }
    }
    
    return record.content || {};
  };

  /**
   * Get localized phases array (for research_projects)
   * @param {Object} record - Research project record
   * @returns {Array} Localized phases array
   */
  const getPhases = (record) => {
    if (!record) return [];
    
    if (isArabic && record.phases_ar && record.phases_ar.length > 0) {
      return record.phases_ar;
    }
    
    return record.phases || [];
  };

  return {
    getField,
    getContent,
    getPhases,
    isArabic
  };
};

export default useLocalizedContent;
